<script lang="ts" setup>
import { computed } from "vue"

type Props = {
    title?: string
    content: string
    severity?: "warn" | "info" | "error" | "secondary"
    position?: "top" | "bottom" | "left" | "right"
    /**
     * Taille de l’icône : variables `--text-*` du thème, transmises à `BaseIcon` via la prop `size`.
     */
    size?: "extra-small" | "small" | "medium" | "large"
}

const props = withDefaults(defineProps<Props>(), {
    title: "",
    severity: "info",
    position: "bottom",
    size: "medium",
})

function getTooltipContent(title: string, content: string) {
    return `
        <div class="base-tooltip-message-wrapper flex flex-col">
            ${title ? `<h4 class="font-bold">${title}</h4>` : ""}
            <p>${content}</p>
        </div>
    `
}

const getTooltipDirectiveValue = computed(() => ({
    value: getTooltipContent(props.title, props.content),
    escape: false,
    pt: {
        text: `whitespace-normal!`,
    },
}))

const getColorBySeverity = computed(() => {
    switch (props.severity) {
        case "warn":
            return "var(--p-orange-500)"
        case "error":
            return "var(--p-red-500)"
        case "secondary":
            return "currentColor"
        default:
            return "var(--p-primary-500)"
    }
})

const getIconBySeverity = computed(() => {
    switch (props.severity) {
        case "warn":
            return "pi pi-exclamation-triangle"
        case "error":
            return "pi pi-exclamation-triangle"
        default:
            return "pi pi-info-circle"
    }
})

/** Taille police icône : variables CSS du thème Tailwind (`@theme` / défauts). */
const getIconSize = computed(() => {
    switch (props.size) {
        case "extra-small":
            return "var(--text-xs)"
        case "small":
            return "var(--text-sm)"
        case "large":
            return "var(--text-lg)"
        case "medium":
        default:
            return "var(--text-base)"
    }
})

const getWrapperClass = computed(() => [
    "base-tooltip-message inline-flex shrink-0 cursor-help items-center pointer-events-auto",
    props.severity === "secondary" ? "text-surface-600" : "",
])
</script>
<template>
    <span :class="getWrapperClass" role="img" :aria-label="content">
        <BaseIcon
            v-if="position === 'top'"
            class="shrink-0"
            :icon="getIconBySeverity"
            :color="getColorBySeverity"
            :size="getIconSize"
            v-tooltip.top="getTooltipDirectiveValue"
        />
        <BaseIcon
            v-else-if="position === 'bottom'"
            class="shrink-0"
            :icon="getIconBySeverity"
            :color="getColorBySeverity"
            :size="getIconSize"
            v-tooltip.bottom="getTooltipDirectiveValue"
        />
        <BaseIcon
            v-else-if="position === 'left'"
            class="shrink-0"
            :icon="getIconBySeverity"
            :color="getColorBySeverity"
            :size="getIconSize"
            v-tooltip.left="getTooltipDirectiveValue"
        />
        <BaseIcon
            v-else
            class="shrink-0"
            :icon="getIconBySeverity"
            :color="getColorBySeverity"
            :size="getIconSize"
            v-tooltip.right="getTooltipDirectiveValue"
        />
    </span>
</template>
